import {
  Button,
  Icon,
  Input,
  Modal,
  RichTextEditor,
  Toast,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { Controller, type FieldErrors, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { cms } from "../../api/cms.service";
import type { CmsPage, CmsBlock } from "../../types/cms";
import { PageHeader } from "../../components/PageHeader";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import { ConfirmationModal } from "../../components/Modal/ConfirmationModal";
import { ROUTES } from "../../routes";
import { usePermissions } from "../../hooks/usePermissions";
import { BlockContentEditor } from "./blocks/BlockContentEditor";
import BlockPreview from "./blocks/BlockPreview";
import { CardEditModal } from "./blocks/CardEditModal";
import { getBlockTypes, getBlockType, getBlockTypesForPage } from "./blocks/blockTypeRegistry";
import { readAnchorId, startingContent, validateBlockContent } from "./blocks/blockValidation";
import { DuplicatePageModal } from "./components/DuplicatePageModal/DuplicatePageModal";
import { ChildPagesMoveWarning } from "./components/PageAddressFields/ChildPagesMoveWarning";
import { PageAddressPreview } from "./components/PageAddressFields/PageAddressPreview";
import { PageSectionField } from "./components/PageAddressFields/PageSectionField";
import { ParentPageField } from "./components/PageAddressFields/ParentPageField";
import { SectionHomeAddress } from "./components/PageAddressFields/SectionHomeAddress";
import {
  type ExportSidebarState,
  PageExportSection,
} from "./components/PageExportSection/PageExportSection";
import { PageLayoutField } from "./components/PageLayoutField/PageLayoutField";
import { ListInSidebarField } from "./components/SidebarField/ListInSidebarField";
import { SidebarField } from "./components/SidebarField/SidebarField";
import { getLayoutOptions, getPageType } from "./pageTypeRegistry";
import {
  firstInvalidField,
  LAYOUT_REQUIRED_MESSAGE,
  validatePageTitle,
  validateSidebar,
} from "./pageLayoutValidation";
import {
  NO_SIDEBAR_SELECTION,
  type SidebarSelection,
  sidebarSelectionFromPage,
  listInSidebarFromPage,
  sidebarListingForSave,
  sidebarOptionsInSection,
  sidebarUpdateForSave,
} from "./pageSidebar";
import { type SidebarOptionsStatus, useSidebarOptions } from "./useSidebarOptions";
import {
  descendantPages,
  isSectionHome,
  isSlugConflict,
  joinPath,
  pagePath,
  parentPageChoices,
  parentPathOf,
  sectionHomeOf,
  SEGMENT_HINT,
  SLUG_TAKEN_MESSAGE,
  validateSegment,
  slugDescribedBy,
} from "./pageAddress";
import { isPreviewablePage } from "./pagePreview";
import {
  DEFAULT_PAGE_SECTION,
  type PageSection,
  pageSectionOf,
  sectionAllowsHome,
} from "./pageSection";
import { type CmsPagesStatus, useCmsPages } from "./useCmsPages";
import { isHomepageFragment } from "./homepageLaunchTile";
import {
  pageOptionsFromFormValues,
  pageOptionsToFormValues,
  type PageMetadataFormValues,
} from "./pageMetadata";
import styles from "./CMS.module.css";

type PageFormValues = PageMetadataFormValues & {
  title: string;
  slug: string;
  section: PageSection;
  sectionHome: boolean;
  parentId: string;
  type: string;
  status: CmsPage["status"];
  sidebar: SidebarSelection;
  listInSidebar: boolean;
  content: string;
  options: string;
};

type BlockFormValues = {
  type: string;
  sortOrder: number;
  options: string;
};

function exportSidebarState(
  menuId: string | null,
  status: SidebarOptionsStatus,
  savedSidebar: { pageCount: number } | undefined,
): ExportSidebarState {
  if (!menuId) {
    return { status: "none" };
  }
  if (status === "loading") {
    return { status: "loading" };
  }
  if (status === "failed" || !savedSidebar) {
    return { status: "failed" };
  }
  return { status: "ready", pageCount: savedSidebar.pageCount };
}

function sectionLockedReason(pagesStatus: CmsPagesStatus) {
  if (pagesStatus === "loading") {
    return "Loading pages…";
  }
  if (pagesStatus === "failed") {
    return "Pages couldn't be loaded. Reload to change the section.";
  }
  return undefined;
}

function pageToFormValues(pageData: CmsPage): PageFormValues {
  return {
    title: pageData.title,
    slug: pageData.segment,
    section: pageSectionOf(pageData),
    sectionHome: isSectionHome(pageData),
    parentId: pageData.parentId ?? "",
    type: pageData.type,
    status: pageData.status,
    sidebar: sidebarSelectionFromPage(pageData),
    listInSidebar: listInSidebarFromPage(pageData),
    content: pageData.content || "",
    ...pageOptionsToFormValues(pageData.options),
  };
}

const defaultFormValues: PageFormValues = {
  title: "",
  slug: "",
  section: DEFAULT_PAGE_SECTION,
  sectionHome: false,
  parentId: "",
  type: "",
  status: "draft",
  sidebar: NO_SIDEBAR_SELECTION,
  listInSidebar: true,
  content: "",
  ...pageOptionsToFormValues(null),
};

function PageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("cms.write");
  const canDelete = hasPermission("cms.delete");

  // Page form
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    getValues,
    setValue,
    setFocus,
    setError: setFieldError,
    formState: { errors, isDirty },
  } = useForm<PageFormValues>({
    shouldFocusError: false,
    defaultValues: defaultFormValues,
  });

  // Page state
  const [page, setPage] = useState<CmsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  const { cmsPages, cmsPagesStatus, reloadCmsPages } = useCmsPages();
  const { sidebarOptions, sidebarOptionsStatus, reloadSidebarOptions } = useSidebarOptions(
    cmsPages,
    cmsPagesStatus,
  );
  const savedSidebar = sidebarOptions.find(option => option.menuId === page?.menuId);
  const exportSidebar = exportSidebarState(
    page?.menuId ?? null,
    sidebarOptionsStatus,
    savedSidebar,
  );

  // Block state
  const [blocks, setBlocks] = useState<CmsBlock[]>([]);
  const [blocksLoading, setBlocksLoading] = useState(true);

  // Block form modal state
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<CmsBlock | null>(null);
  const [blockContent, setBlockContent] = useState<Record<string, unknown> | null>(null);
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockUploading, setBlockUploading] = useState(false);
  const [cardUploading, setCardUploading] = useState(false);
  const [showBlockErrors, setShowBlockErrors] = useState(false);

  const {
    register: registerBlock,
    handleSubmit: handleBlockSubmit,
    reset: resetBlock,
    watch: watchBlock,
    setValue: setBlockValue,
  } = useForm<BlockFormValues>({
    defaultValues: { type: getBlockTypes()[0]?.key ?? "", sortOrder: 0, options: "" },
  });

  const pageTypeValue = watch("type");
  const pageTitleValue = watch("title");
  const slugValue = watch("slug");
  const sectionValue = watch("section");
  const parentIdValue = watch("parentId");
  const sectionHomeValue = watch("sectionHome");
  const parentChoices = parentPageChoices(cmsPages, id ?? null, sectionValue);
  const savedParentPath = page ? parentPathOf(page) : null;
  const chosenParentPath =
    parentChoices.find(choice => choice.id === parentIdValue)?.slug ??
    (page && parentIdValue && parentIdValue === page.parentId ? savedParentPath : null);
  const childPages = id ? descendantPages(cmsPages, id) : [];
  const selectedPageType = getPageType(pageTypeValue);
  const layoutOptions = getLayoutOptions(page?.type ?? "", sectionValue);
  const blockTypeValue = watchBlock("type");
  const otherAnchorIds = blocks
    .filter(block => block.id !== editingBlock?.id)
    .map(block => readAnchorId(block.content))
    .filter((anchorId): anchorId is string => anchorId !== null);
  const blockErrors = showBlockErrors
    ? validateBlockContent(blockTypeValue, blockContent, otherAnchorIds)
    : {};
  const blockTypeChoices = getBlockTypesForPage(pageTypeValue, editingBlock?.type);

  // Delete state
  const [showDeletePage, setShowDeletePage] = useState(false);
  const [showDuplicatePage, setShowDuplicatePage] = useState(false);
  const [previewOpening, setPreviewOpening] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState<CmsBlock | null>(null);

  // Card-level editing state
  const [editingCardBlock, setEditingCardBlock] = useState<CmsBlock | null>(null);
  const [editingCardIndex, setEditingCardIndex] = useState<number | null>(null);
  const [cardMode, setCardMode] = useState<"edit" | "create">("edit");
  const [cardToDelete, setCardToDelete] = useState<{ block: CmsBlock; index: number } | null>(null);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  function showToast(message: string, type: ToastType) {
    setToast({ message, type });
  }

  // Load page and menus
  useEffect(() => {
    if (id) {
      loadPage(id);
      loadBlocks(id);
    }
  }, [id]);

  async function loadPage(pageId: string) {
    try {
      setLoading(true);
      setError(null);
      const pageData = await cms.getPageById(pageId);
      setPage(pageData);
      reset(pageToFormValues(pageData));
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load page"));
    } finally {
      setLoading(false);
    }
  }

  async function loadBlocks(pageId: string) {
    try {
      setBlocksLoading(true);
      const blockData = await cms.getBlocksByPageId(pageId);
      setBlocks(blockData.sort((a, b) => a.sortOrder - b.sortOrder));
    } catch {
      setBlocks([]);
    } finally {
      setBlocksLoading(false);
    }
  }

  // Save page
  async function handleSavePage(data: PageFormValues) {
    if (!id || !canWrite || blockUploading || cardUploading) return;

    setSaving(true);
    try {
      const parsedOptions = pageOptionsFromFormValues(data, data.options);

      const updated = await cms.updatePage(id, {
        title: data.title.trim(),
        section: data.section,
        ...(data.sectionHome
          ? { sectionHome: true }
          : { slug: data.slug.trim(), parentId: data.parentId || null }),
        type: data.type,
        status: data.status,
        ...(page ? sidebarUpdateForSave(data.type, data.sidebar, page, data.title) : {}),
        content: data.content || null,
        ...sidebarListingForSave(data.type, data.listInSidebar, parsedOptions),
      });
      setPage(updated);
      reset(pageToFormValues(updated));
      reloadSidebarOptions();
      reloadCmsPages();
      showToast("Page saved successfully", "success");
    } catch (err) {
      const sectionChanged = page !== null && data.section !== pageSectionOf(page);
      if (sectionChanged && isSlugConflict(err)) {
        reloadCmsPages();
      } else if (isSlugConflict(err) && !data.sectionHome) {
        setFieldError(
          "slug",
          { type: "server", message: SLUG_TAKEN_MESSAGE },
          { shouldFocus: true },
        );
        return;
      }
      showToast(`Failed to save: ${err instanceof Error ? err.message : "Unknown error"}`, "error");
    } finally {
      setSaving(false);
    }
  }

  function focusFirstInvalidField(invalidFields: FieldErrors<PageFormValues>) {
    const field = firstInvalidField(invalidFields);
    if (field) {
      setFocus(field);
    }
  }

  // Delete page
  async function handleDeletePage() {
    if (!id) return;
    await cms.deletePage(id);
    showToast("Page deleted", "success");
    navigate(ROUTES.cms.pages);
  }

  function handleDuplicated(copy: CmsPage) {
    setShowDuplicatePage(false);
    reloadCmsPages();
    showToast("Page duplicated. You're now editing the copy.", "success");
    navigate(ROUTES.cms.pageDetail(copy.id));
  }

  async function handlePreview() {
    if (!id || previewOpening) return;
    const previewWindow = window.open("", "_blank");
    if (previewWindow) {
      previewWindow.opener = null;
    }
    setPreviewOpening(true);
    try {
      const { url } = await cms.createPagePreviewLink(id);
      if (!previewWindow) {
        showToast("Your browser blocked the preview window. Allow pop-ups and try again.", "error");
        return;
      }
      previewWindow.location.href = url;
    } catch (err) {
      previewWindow?.close();
      showToast(
        `Failed to open preview: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setPreviewOpening(false);
    }
  }

  // Block CRUD
  function handleOpenAddBlock() {
    setShowBlockErrors(false);
    setEditingBlock(null);
    setBlockContent(null);
    resetBlock({
      type: getBlockTypes()[0]?.key ?? "",
      sortOrder: blocks.length > 0 ? Math.max(...blocks.map(b => b.sortOrder)) + 1 : 0,
      options: "",
    });
    setIsBlockModalOpen(true);
  }

  function handleOpenEditBlock(block: CmsBlock) {
    setShowBlockErrors(false);
    setEditingBlock(block);
    setBlockContent(block.content ?? null);
    resetBlock({
      type: block.type,
      sortOrder: block.sortOrder,
      options: block.options ? JSON.stringify(block.options, null, 2) : "",
    });
    setIsBlockModalOpen(true);
  }

  function handleCloseBlockModal() {
    setIsBlockModalOpen(false);
    setBlockUploading(false);
    setEditingBlock(null);
  }

  async function handleSaveBlock(data: BlockFormValues) {
    if (!id || !canWrite || blockUploading) return;

    if (Object.keys(validateBlockContent(data.type, blockContent, otherAnchorIds)).length > 0) {
      setShowBlockErrors(true);
      return;
    }

    setBlockSaving(true);

    let parsedOptions: Record<string, unknown> | null = null;
    if (data.options.trim()) {
      try {
        parsedOptions = JSON.parse(data.options);
      } catch {
        showToast("Invalid JSON in block options", "error");
        setBlockSaving(false);
        return;
      }
    }

    try {
      if (editingBlock) {
        await cms.updateBlock(editingBlock.id, {
          type: data.type,
          content: blockContent,
          options: parsedOptions,
          sortOrder: data.sortOrder,
        });
        showToast("Block updated", "success");
      } else {
        await cms.createBlock({
          type: data.type,
          pageId: id,
          content: blockContent,
          options: parsedOptions,
          sortOrder: data.sortOrder,
        });
        showToast("Block added", "success");
      }
      handleCloseBlockModal();
      await loadBlocks(id);
    } catch (err) {
      showToast(
        `Failed to save block: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setBlockSaving(false);
    }
  }

  async function handleDeleteBlock() {
    if (!blockToDelete || !id) return;
    await cms.deleteBlock(blockToDelete.id);
    setBlockToDelete(null);
    showToast("Block deleted", "success");
    await loadBlocks(id);
  }

  if (loading) return <LoadingState message="Loading page..." />;

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load page"
        message={error.message}
        action={{ label: "Back to pages", onClick: () => navigate(ROUTES.cms.pages) }}
      />
    );
  }

  if (!page) {
    return (
      <EmptyState
        variant="empty"
        title="Page not found"
        message="The requested page could not be found."
        action={{ label: "Back to pages", onClick: () => navigate(ROUTES.cms.pages) }}
      />
    );
  }

  if (isHomepageFragment(page)) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title={page.title} subtitle={`/${page.slug}`} />

        <div className={styles.detailWrapper}>
          <div className={styles.section}>
            <p className={styles.homepageNotice}>This page is edited on the Homepage screen.</p>
            <Link className={styles.homepageLink} to={ROUTES.cms.homepage}>
              Go to the Homepage screen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const canPreview = canWrite && isPreviewablePage(page);

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={page.title}
        subtitle={pagePath(page)}
        meta={
          canPreview && isDirty ? (
            <span id="preview-saved-version-hint" className={styles.previewHint}>
              Preview shows the last saved version. Save to include your changes.
            </span>
          ) : undefined
        }
      >
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {canPreview && (
            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={previewOpening}
              aria-busy={previewOpening}
              aria-describedby={isDirty ? "preview-saved-version-hint" : undefined}
              title="Previews the last saved version"
            >
              {previewOpening ? "Opening preview…" : "Preview"}
            </Button>
          )}
          {canWrite && (
            <Button variant="outlined" onClick={() => setShowDuplicatePage(true)}>
              Duplicate page
            </Button>
          )}
          <Button variant="outlined" color="default" onClick={() => navigate(ROUTES.cms.pages)}>
            Back to Pages
          </Button>
        </div>
      </PageHeader>

      <div className={styles.detailWrapper}>
        {/* Page Edit Form */}
        <form
          className={styles.section}
          onSubmit={handleSubmit(handleSavePage, focusFirstInvalidField)}
        >
          <h2 className={styles.sectionTitle}>Page Details</h2>

          <div className={styles.fieldGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="edit-title">
                Title <span className={styles.required}>*</span>
              </label>
              <Input
                id="edit-title"
                {...register("title", { validate: validatePageTitle })}
                error={errors.title?.message}
              />
            </div>

            <Controller
              name="section"
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <PageSectionField
                  id="edit-section"
                  value={value}
                  lockedReason={sectionLockedReason(cmsPagesStatus)}
                  onChange={section => {
                    onChange(section);
                    if (parentIdValue) {
                      setValue("parentId", "", { shouldDirty: true });
                    }
                    if (getValues("sidebar").choice === "join") {
                      setValue("sidebar", NO_SIDEBAR_SELECTION, { shouldDirty: true });
                    }
                    if (!sectionAllowsHome(section) || sectionHomeOf(cmsPages, section, page.id)) {
                      setValue("sectionHome", false);
                    }
                  }}
                  sectionHome={sectionHomeValue}
                  onSectionHomeChange={sectionHome =>
                    setValue("sectionHome", sectionHome, { shouldDirty: true })
                  }
                  existingHome={sectionHomeOf(cmsPages, value, page.id)}
                  inputRef={ref}
                />
              )}
            />

            {sectionHomeValue ? (
              <div className={styles.formField}>
                <SectionHomeAddress id="edit-slug" section={sectionValue} />
                <ChildPagesMoveWarning
                  childPages={childPages}
                  oldSection={pageSectionOf(page)}
                  newSection={sectionValue}
                  oldPath={page.slug}
                  newPath=""
                />
              </div>
            ) : (
              <>
                <Controller
                  name="parentId"
                  control={control}
                  render={({ field: { value, onChange, ref } }) => (
                    <ParentPageField
                      id="edit-parent"
                      section={sectionValue}
                      pages={parentChoices}
                      pagesStatus={cmsPagesStatus}
                      value={value}
                      savedParentPath={page.parentId === value ? savedParentPath : null}
                      onChange={onChange}
                      inputRef={ref}
                    />
                  )}
                />

                <div className={styles.formField}>
                  <label className={styles.formLabel} htmlFor="edit-slug">
                    Slug <span className={styles.required}>*</span>
                  </label>
                  <Input
                    id="edit-slug"
                    {...register("slug", {
                      validate: (value, values) =>
                        values.sectionHome
                          ? true
                          : validateSegment(value, {
                              section: values.section,
                              pages: cmsPagesStatus === "ready" ? cmsPages : null,
                              editedPageId: id ?? null,
                            }),
                    })}
                    error={errors.slug?.message}
                    aria-describedby={slugDescribedBy("edit-slug", Boolean(errors.slug))}
                  />
                  <span id="edit-slug-hint" className={styles.fieldHint}>
                    {SEGMENT_HINT}
                  </span>
                  <PageAddressPreview
                    id="edit-slug-address"
                    section={sectionValue}
                    parentSlug={chosenParentPath}
                    segment={slugValue}
                  />
                  <ChildPagesMoveWarning
                    childPages={childPages}
                    oldSection={pageSectionOf(page)}
                    newSection={sectionValue}
                    oldPath={page.slug}
                    newPath={joinPath(chosenParentPath, slugValue.trim())}
                  />
                </div>
              </>
            )}

            <div className={styles.fieldFull}>
              <Controller
                name="type"
                control={control}
                rules={{ required: LAYOUT_REQUIRED_MESSAGE }}
                render={({ field: { value, onChange, ref } }) => (
                  <PageLayoutField
                    name="edit-layout"
                    value={value}
                    options={layoutOptions}
                    onChange={onChange}
                    error={errors.type?.message}
                    inputRef={ref}
                  />
                )}
              />
            </div>

            {selectedPageType?.hasSubmenu && (
              <div className={styles.fieldFull}>
                <Controller
                  name="sidebar"
                  control={control}
                  rules={{ validate: validateSidebar }}
                  render={({ field: { value, onChange, ref } }) => (
                    <SidebarField
                      name="edit-sidebar"
                      options={sidebarOptionsInSection(sidebarOptions, sectionValue)}
                      optionsStatus={sidebarOptionsStatus}
                      value={value}
                      onChange={onChange}
                      pageTitle={pageTitleValue}
                      renameMenuId={page.menuId}
                      error={errors.sidebar?.message}
                      inputRef={ref}
                    />
                  )}
                />
                <Controller
                  name="listInSidebar"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <ListInSidebarField
                      id="edit-list-in-sidebar"
                      checked={value}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
            )}

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="edit-status">
                Status <span className={styles.required}>*</span>
              </label>
              <select id="edit-status" className={styles.selectField} {...register("status")}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="page-meta-title">
                Meta title
              </label>
              <Input
                id="page-meta-title"
                {...register("metaTitle")}
                aria-describedby="page-meta-title-hint"
              />
              <span id="page-meta-title-hint" className={styles.fieldHint}>
                Leave blank to use the page title
              </span>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="page-meta-description">
                Meta description
              </label>
              <Input
                id="page-meta-description"
                {...register("metaDescription")}
                aria-describedby="page-meta-description-hint"
              />
              <span id="page-meta-description-hint" className={styles.fieldHint}>
                Leave blank to use the default site description
              </span>
            </div>

            <div className={`${styles.formField} ${styles.fieldFull}`}>
              <Controller
                name="content"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <RichTextEditor
                    value={value}
                    onChange={onChange}
                    preset="full"
                    headingIds
                    htmlTab
                    label="Content"
                    minHeight={300}
                  />
                )}
              />
            </div>

            <div className={`${styles.formField} ${styles.fieldFull}`}>
              <label className={styles.formLabel} htmlFor="edit-options">
                Options (JSON)
              </label>
              <textarea
                id="edit-options"
                aria-describedby="edit-options-hint"
                className={styles.textarea}
                {...register("options")}
                placeholder="{}"
                rows={4}
              />
              <span id="edit-options-hint" className={styles.fieldHint}>
                Meta title and Meta description are managed by the dedicated fields above.
              </span>
            </div>
          </div>
        </form>

        {/* Blocks Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
              Blocks
            </h2>
            {canWrite && (
              <Button variant="contained" color="primary" onClick={handleOpenAddBlock}>
                <Icon icon="plus" size="small" />
                Add Block
              </Button>
            )}
          </div>

          {blocksLoading ? (
            <LoadingState message="Loading blocks..." />
          ) : blocks.length === 0 ? (
            <p className={styles.emptyBlocks}>No blocks yet. Add a block to build this page.</p>
          ) : (
            <div className={styles.blockList}>
              {blocks.map(block => (
                <div key={block.id} className={styles.blockItem}>
                  <div className={styles.blockHeader}>
                    <div className={styles.blockMeta}>
                      <span className={styles.blockTypeBadge}>
                        {block.type.replace("block_", "")}
                      </span>
                      <span className={styles.blockOrder}>#{block.sortOrder}</span>
                    </div>
                    {(canWrite || canDelete) && (
                      <div className={styles.blockActions}>
                        {canDelete && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setBlockToDelete(block)}
                            aria-label="Delete block"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={styles.blockPreview}>
                    <BlockPreview
                      block={block}
                      onEditCard={
                        canWrite
                          ? cardIndex => {
                              setCardMode("edit");
                              setEditingCardBlock(block);
                              setEditingCardIndex(cardIndex);
                            }
                          : undefined
                      }
                      onAddCard={
                        canWrite
                          ? () => {
                              setCardMode("create");
                              setEditingCardBlock(block);
                              setEditingCardIndex(-1);
                            }
                          : undefined
                      }
                      onDeleteCard={
                        canWrite ? index => setCardToDelete({ block, index }) : undefined
                      }
                      onEditBlock={canWrite ? () => handleOpenEditBlock(block) : undefined}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <PageExportSection pageId={page.id} sidebar={exportSidebar} />

        {/* Page Actions */}
        {canWrite && (
          <div className={styles.formActions}>
            {canDelete && (
              <div className={styles.formActionsLeft}>
                <Button variant="outlined" color="error" onClick={() => setShowDeletePage(true)}>
                  Delete Page
                </Button>
              </div>
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={saving || blockUploading || cardUploading || !isDirty}
              onClick={handleSubmit(handleSavePage, focusFirstInvalidField)}
            >
              {saving ? "Saving..." : "Save Page"}
            </Button>
          </div>
        )}
      </div>

      {/* Block Edit/Add Modal */}
      {isBlockModalOpen && (
        <Modal
          isOpen={isBlockModalOpen}
          onClose={handleCloseBlockModal}
          title={editingBlock ? "Edit Block" : "Add Block"}
          size="medium"
        >
          <form
            onSubmit={handleBlockSubmit(handleSaveBlock)}
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="block-type">
                Block Type
              </label>
              <select
                id="block-type"
                className={styles.selectField}
                {...registerBlock("type")}
                onChange={e => {
                  const newType = e.target.value;
                  setBlockValue("type", newType);
                  const definition = getBlockType(newType);
                  if (definition) {
                    setBlockContent(
                      editingBlock
                        ? startingContent(newType, definition.defaultContent, blockContent)
                        : definition.defaultContent,
                    );
                  }
                }}
              >
                {blockTypeChoices.map(bt => (
                  <option key={bt.key} value={bt.key}>
                    {bt.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="block-sort-order">
                Sort Order
              </label>
              <Input
                id="block-sort-order"
                type="number"
                {...registerBlock("sortOrder", { valueAsNumber: true })}
              />
            </div>

            <BlockContentEditor
              key={blockTypeValue}
              errors={blockErrors}
              blockType={blockTypeValue}
              pageType={pageTypeValue}
              value={blockContent}
              onChange={setBlockContent}
              onUploadingChange={setBlockUploading}
            />

            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="block-options-json">
                Options (JSON)
              </label>
              <textarea
                id="block-options-json"
                className={styles.textarea}
                {...registerBlock("options")}
                placeholder="{}"
                rows={3}
              />
            </div>

            <div className={styles.formActions}>
              <Button
                variant="outlined"
                color="default"
                onClick={handleCloseBlockModal}
                disabled={blockSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={blockSaving || blockUploading}
              >
                {blockSaving ? "Saving..." : editingBlock ? "Update Block" : "Add Block"}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {showDuplicatePage && (
        <DuplicatePageModal
          page={page}
          pages={cmsPagesStatus === "ready" ? cmsPages : null}
          onClose={() => setShowDuplicatePage(false)}
          onDuplicated={handleDuplicated}
        />
      )}

      {/* Delete Page Confirmation */}
      {showDeletePage && (
        <ConfirmationModal
          title="Delete Page"
          message={`Are you sure you want to delete "${page.title}"?`}
          warningMessage="This action cannot be undone. All blocks belonging to this page will also be deleted."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setShowDeletePage(false)}
          onConfirm={handleDeletePage}
        />
      )}

      {/* Delete Block Confirmation */}
      {blockToDelete && (
        <ConfirmationModal
          title="Delete Block"
          message={`Are you sure you want to delete this ${blockToDelete.type} block?`}
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setBlockToDelete(null)}
          onConfirm={handleDeleteBlock}
        />
      )}

      {cardToDelete && (
        <ConfirmationModal
          title="Delete Card"
          message="Are you sure you want to delete this card?"
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setCardToDelete(null)}
          onConfirm={async () => {
            const cards = cardToDelete.block.content?.cards;
            if (!canWrite || !Array.isArray(cards)) return;
            await cms.updateBlock(cardToDelete.block.id, {
              content: {
                ...cardToDelete.block.content,
                cards: cards.filter((_, index) => index !== cardToDelete.index),
              },
            });
            setCardToDelete(null);
            if (id) await loadBlocks(id);
          }}
        />
      )}

      {/* Card-level Edit Modal */}
      {editingCardBlock && editingCardIndex !== null && (
        <CardEditModal
          key={`${editingCardBlock.id}:${cardMode}:${editingCardIndex}`}
          mode={cardMode}
          onUploadingChange={setCardUploading}
          block={editingCardBlock}
          cardIndex={editingCardIndex}
          onClose={() => {
            setEditingCardBlock(null);
            setEditingCardIndex(null);
          }}
          onSaved={() => {
            setEditingCardBlock(null);
            setEditingCardIndex(null);
            if (id) {
              void loadBlocks(id);
            }
          }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default PageDetailPage;
